import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import QueueDialer from '@/components/queue/QueueDialer';
import QueueWorkModal from '@/components/queue/QueueWorkModal';
import SweeperWorkspace from '@/components/queue/SweeperWorkspace';
import ArtistAvailabilityHeatmap from '@/components/queue/ArtistAvailabilityHeatmap';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import {
  buildQueue,
  buildHeatmap,
  laneOf,
  partitionQueue,
  type GapFilter,
  type HeatmapCell,
  type QueueChannel,
  type QueueItem,
} from '@/domain/queueEngine';
import type { Channel, ConversationEntry, CustomerRecord } from '@/domain/types';
import { relativeFromIso, simTimeShort } from '@/utils/format';
import {
  Target,
  XCircle,
  Phone,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Lock,
  Bell,
  Clock,
  PhoneMissed,
  CircleDollarSign,
  CheckCircle2,
  Ban,
} from 'lucide-react';
import { buildTopupTrack } from '@/domain/topupReminders';
import { format } from 'date-fns';
import clsx from 'clsx';

// Mirrors the composer's private channel union so we can forward the focused
// channel straight through to the work modal without widening the type. A
// QueueChannel minus 'voice' is exactly this set.
type MessageChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

// Primary sequence tabs, driven by each item's sequence-clock stage / red state
// (see stageOf). First/Second/Third are the three call-sequence attempts; Reds
// collects the terminal states (never-reached + said-no).
type SeqTab = 'first' | 'second' | 'third' | 'reds';

const SEQ_TABS: Array<{ key: SeqTab; label: string }> = [
  { key: 'first', label: 'First' },
  { key: 'second', label: 'Second' },
  { key: 'third', label: 'Third' },
  { key: 'reds', label: 'Reds' },
];

const SEQ_LABEL: Record<SeqTab, string> = {
  first: 'First',
  second: 'Second',
  third: 'Third',
  reds: 'Reds',
};

// The agent works a small, finite working set at a time rather than the whole
// agent-lane list (bd tv-6s3, board 05). As items clear (leave the derived
// queue) the next tail item fills in; when the whole list clears, buildQueue
// re-derives a fresh batch. Replies + missed calls surface in as they arrive.
const WORKING_SET_SIZE = 6;

// How long the "sequence cleared" success banner shows before the next sequence
// loads automatically (bd tv-1x7). UI timing, not domain time.
export const HANDOFF_DELAY_MS = 2200;

// A reactive event on the Alerts tab (bd tv-3wr.2). 'no-deposit-declined' (bd
// tv-aj0) is fed from active NoDepositDeclineFlags: Ops declined a no-money
// override, so the agent must chase an actual deposit.
type AlertKind = 'no-deposit-declined' | 'missed-call' | 'missed-topup' | 'new-reply';

interface AlertRow {
  id: string;
  alertKind: AlertKind;
  customer: CustomerRecord;
  detail: string;
  whenIso?: string;
  action: 'call' | 'chase' | 'open';
}

const ALERT_META: Record<AlertKind, { label: string; action: string }> = {
  'no-deposit-declined': { label: 'No-deposit declined', action: 'Collect deposit' },
  'missed-call': { label: 'Missed call', action: 'Call back' },
  'missed-topup': { label: 'Top-up missed', action: 'Chase' },
  'new-reply': { label: 'New reply', action: 'Open' },
};

/**
 * Which sequence tab an item belongs to. Items with no sequence-clock state fall
 * to stage 1 (First); any red state (either red kind, or a bare 'red' stage)
 * lands in Reds.
 */
function stageOf(item: QueueItem): SeqTab {
  if (item.redKind || item.stage === 'red') return 'reds';
  const stage = item.stage ?? 1;
  return stage === 3 ? 'third' : stage === 2 ? 'second' : 'first';
}

// Secondary filters WITHIN the active sequence tab, over the AGENT-lane main list
// only (bd tv-570). AGENT_KIND_GROUPS lives in the shared kindGroups module: the
// money and open-business chips moved to the Sweeper, so only the fresh-lane
// Messages chip remains here. Chips hide when their count in the tab is 0.

export default function OperatorQueue() {
  const navigate = useNavigate();
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const healingCheckTasks = useBrainStore((s) => s.healingCheckTasks);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  const studios = useBrainStore((s) => s.studios);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const noDepositDeclineFlags = useBrainStore((s) => s.noDepositDeclineFlags);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const activeStudio = useSessionStore((s) => s.activeStudio);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // The agent works one studio at a time (board 05). Scope every surface to the
  // active studio from the top-bar switcher, falling back to all assigned.
  const scopedStudios = useMemo(
    () => (activeStudio ? [activeStudio] : assignedStudios),
    [activeStudio, assignedStudios],
  );

  const [gapFilter, setGapFilter] = useState<GapFilter | null>(null);
  const [seqTab, setSeqTab] = useState<SeqTab>('first');
  const [showAvailability, setShowAvailability] = useState(false);
  const [modalItem, setModalItem] = useState<QueueItem | null>(null);
  const [modalChannel, setModalChannel] = useState<MessageChannel | undefined>(undefined);
  // The agent's gap-fill surface (next week onwards, own studios).
  const [nextGaps, setNextGaps] = useState(false);
  // Alerts tab: reactive event-driven items collect here, out of the working set
  // (bd tv-3wr.2). Fifth tab alongside First/Second/Third/Reds.
  const [showAlerts, setShowAlerts] = useState(false);
  // Which sequences have already auto-advanced once. A sequence auto-advances the
  // FIRST time it clears; coming back to it later (by choice, or because new
  // leads landed and were worked) must not auto-advance again (bd tv-1x7).
  const [autoAdvanced, setAutoAdvanced] = useState<Record<SeqTab, boolean>>({
    first: false,
    second: false,
    third: false,
    reds: false,
  });

  const queue = useMemo(
    () =>
      buildQueue({
        customers,
        bookings,
        deposits,
        pieces,
        artists,
        rebookFollowupTasks,
        healingCheckTasks,
        topupPlans,
        studioIds: scopedStudios,
        nowIso: clockIso,
        gapFilter: gapFilter ?? undefined,
        sequenceState,
      }),
    [
      customers,
      bookings,
      deposits,
      pieces,
      artists,
      rebookFollowupTasks,
      healingCheckTasks,
      topupPlans,
      scopedStudios,
      clockIso,
      gapFilter,
      sequenceState,
    ],
  );

  const heatmapRows = useMemo(
    () => buildHeatmap({ artists, bookings, nowIso: clockIso, studioIds: scopedStudios }),
    [artists, bookings, clockIso, scopedStudios],
  );

  // Standard-mode sections. In gap mode the queue is a flat candidate list and
  // this partition is unused.
  const { scheduledDue, main, scheduledCalls } = useMemo(
    () => partitionQueue(gapFilter ? [] : queue, clockIso),
    [queue, gapFilter, clockIso],
  );

  // The Agent works only its own lane (bd tv-570): fresh inquiries and pushed
  // leads. The money + open-business tail (deposits, unfinished pieces, rebooks,
  // healing checks) is Sweeper work and is filtered out of every agent surface
  // below — counts, tabs, chips and reds all read from agentMain.
  const agentMain = useMemo(
    () => main.filter((item) => laneOf(item.kind) === 'agent'),
    [main],
  );

  // Per-sequence-tab counts over the whole agent-lane main list.
  const seqCounts = useMemo(() => {
    const counts: Record<SeqTab, number> = { first: 0, second: 0, third: 0, reds: 0 };
    for (const item of agentMain) counts[stageOf(item)] += 1;
    return counts;
  }, [agentMain]);

  // Agent-lane items in the active (non-red) sequence tab.
  const mainForTab = useMemo(
    () => agentMain.filter((item) => stageOf(item) === seqTab),
    [agentMain, seqTab],
  );

  // Split the active sequence tab: leads still to call vs. contacted leads now
  // in their 24h cooldown ("completed this sequence", bd tv-jpx).
  const activeForTab = useMemo(() => mainForTab.filter((i) => !i.dormant), [mainForTab]);
  const completedForTab = useMemo(() => mainForTab.filter((i) => i.dormant), [mainForTab]);

  // The finite working set: the first WORKING_SET_SIZE of the active leads.
  // Actioning an item drops it from the derived list, so slicing again reveals
  // the next tail item — "reveal on clear", keeping ~6 in view (bd tv-6s3).
  const workingSet = useMemo(
    () => activeForTab.slice(0, WORKING_SET_SIZE),
    [activeForTab],
  );
  const lockedCount = activeForTab.length - workingSet.length;

  // A call sequence is "cleared" once every active lead is contacted (none left
  // to call) but the agent actually worked leads into it (bd tv-1x7).
  const clearedSequence =
    !gapFilter &&
    !showAlerts &&
    seqTab !== 'reds' &&
    activeForTab.length === 0 &&
    completedForTab.length > 0;

  // Auto-advance only the first time a cleared sequence is reached. Once a
  // sequence has advanced, returning to it (manually, or after fresh leads land
  // and clear again) shows the cleared banner but never auto-advances again.
  const willAutoAdvance = clearedSequence && seqTab !== 'third' && !autoAdvanced[seqTab];

  // Automatic hand-off: after the success banner shows, load the next sequence
  // (First -> Second -> Third). The third doesn't auto-advance; it offers the
  // future-gaps board instead.
  useEffect(() => {
    if (!willAutoAdvance) return;
    const from = seqTab;
    const next: SeqTab = from === 'first' ? 'second' : 'third';
    const timer = setTimeout(() => {
      setAutoAdvanced((prev) => ({ ...prev, [from]: true }));
      setSeqTab(next);
    }, HANDOFF_DELAY_MS);
    return () => clearTimeout(timer);
  }, [willAutoAdvance, seqTab]);

  // Reds tab is split into two spaces: light reds are still callable, dark reds
  // (said-no) are handed to automated outreach and rendered read-only. Both read
  // from the agent lane, so a money-cohort red belongs to the Sweeper, not here.
  const lightReds = useMemo(() => agentMain.filter((i) => i.redKind === 'never-reached'), [agentMain]);
  const darkReds = useMemo(() => agentMain.filter((i) => i.redKind === 'said-no'), [agentMain]);

  // "Replies to action" rail: queued people (a sequence record exists) whose latest
  // message is an unanswered inbound reply, newest first. Fully derived, so a row
  // clears itself the moment the agent responds (the latest entry becomes outbound).
  const repliesToAction = useMemo(() => {
    const rows: Array<{ customer: CustomerRecord; entry: ConversationEntry }> = [];
    for (const customerId of Object.keys(sequenceState)) {
      const c = customers[customerId];
      if (!c) continue;
      const last = c.conversation[c.conversation.length - 1];
      if (!last || last.direction !== 'in') continue;
      rows.push({ customer: c, entry: last });
    }
    rows.sort((a, b) => b.entry.timestamp.localeCompare(a.entry.timestamp));
    return rows;
  }, [sequenceState, customers]);

  // Alerts feed (bd tv-3wr.2): the reactive, event-driven items — missed calls,
  // missed top-ups and new inbound replies. They collect on the Alerts tab as
  // they happen rather than silently reshuffling the six-item working set, then
  // batch in when the agent promotes them. One row per customer (missed-call
  // beats missed-top-up beats new-reply if the same person triggers two).
  const alerts = useMemo<AlertRow[]>(() => {
    const byCustomer = new Map<string, AlertRow>();

    // No-deposit declines (bd tv-aj0) — Ops declined a no-money override, so the
    // agent must chase an actual deposit (no second trip through Ops). Active
    // (unresolved) flags only; resolving one (deposit paid) drops the row on the
    // next read. Set first so a decline outranks any other event for the same
    // person — it is money-blocking.
    for (const flag of Object.values(noDepositDeclineFlags)) {
      if (flag.resolvedAt) continue;
      const c = customers[flag.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-decline-${c.id}`,
        alertKind: 'no-deposit-declined',
        customer: c,
        detail: flag.reason
          ? `No-deposit override declined · ${flag.reason}`
          : 'No-deposit override declined; collect a deposit to book',
        whenIso: flag.createdAt,
        action: 'chase',
      });
    }

    // Missed calls — already agent-lane main items.
    for (const item of agentMain) {
      if (item.kind !== 'missed-call') continue;
      if (byCustomer.has(item.customer.id)) continue;
      const last = item.customer.conversation[item.customer.conversation.length - 1];
      byCustomer.set(item.customer.id, {
        id: `alert-missed-${item.customer.id}`,
        alertKind: 'missed-call',
        customer: item.customer,
        detail: item.reason,
        whenIso: last?.timestamp,
        action: 'call',
      });
    }

    // Missed top-ups — partial deposits due for a chase, own studio only.
    const topups = buildTopupTrack({
      nowIso: clockIso,
      deposits,
      bookings,
      customers,
      studioIds: scopedStudios,
    });
    for (const row of topups) {
      if (!row.dueForReminder) continue;
      const c = customers[row.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-topup-${c.id}`,
        alertKind: 'missed-topup',
        customer: c,
        detail: `$${row.outstandingAud.toLocaleString()} instalment outstanding${
          row.sessionIso ? `, ${format(new Date(row.sessionIso), 'EEE d MMM')} session` : ''
        }`,
        whenIso: row.sessionIso,
        action: 'chase',
      });
    }

    // New inbound replies — queued leads whose latest message is unanswered.
    for (const { customer: c, entry } of repliesToAction) {
      if (byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-reply-${c.id}`,
        alertKind: 'new-reply',
        customer: c,
        detail: `${REPLY_CHANNEL_LABEL[entry.channel]}: "${entry.body}"`,
        whenIso: entry.timestamp,
        action: 'open',
      });
    }

    // Money-blocking declines first, then urgent (calls, top-ups) above replies;
    // newest first within each.
    const order: Record<AlertRow['alertKind'], number> = {
      'no-deposit-declined': 0,
      'missed-call': 1,
      'missed-topup': 2,
      'new-reply': 3,
    };
    return [...byCustomer.values()].sort((a, b) => {
      if (order[a.alertKind] !== order[b.alertKind]) return order[a.alertKind] - order[b.alertKind];
      return (b.whenIso ?? '').localeCompare(a.whenIso ?? '');
    });
  }, [
    agentMain,
    repliesToAction,
    deposits,
    bookings,
    customers,
    scopedStudios,
    clockIso,
    noDepositDeclineFlags,
  ]);

  // Work an alert: open its work modal when the customer has a queue card, else
  // fall back to the full record.
  const onOpenAlert = (row: AlertRow) => {
    const item = main.find((i) => i.customer.id === row.customer.id);
    if (item) onOpen(item);
    else navigate(`/customer/${row.customer.id}`);
  };

  // Finishable-batch header tiles: leads still to call vs. those already worked
  // this sequence (contacted, now in their 24h cooldown).
  const toCallCount = agentMain.filter((q) => !q.dormant && !q.redKind).length;
  const doneCount = agentMain.filter((q) => q.dormant).length;
  const filterArtist = gapFilter ? artists[gapFilter.artistId] : null;

  const onSelectCell = (cell: HeatmapCell) => {
    setGapFilter({ artistId: cell.artistId, dayIso: cell.dayIso });
  };

  // Every dialer opens the work modal instead of navigating. Voice opens the
  // modal but leaves the composer channel inferred (the card's voice button
  // already starts the call); other channels focus the composer on that channel.
  const onOpen = (item: QueueItem, opts?: { channel?: QueueChannel }) => {
    setModalItem(item);
    setModalChannel(opts?.channel && opts.channel !== 'voice' ? opts.channel : undefined);
  };

  const onSwitchSeqTab = (key: SeqTab) => {
    setSeqTab(key);
    setShowAlerts(false);
  };

  // Batched promotion (bd tv-3wr.2): pull the collected alerts into the working
  // set and drop the agent back onto the First tab to work them.
  const promoteAlerts = () => {
    setShowAlerts(false);
    setSeqTab('first');
  };

  if (nextGaps) {
    return (
      <section className="px-10 py-8" data-testid="operator-queue-screen">
        <button
          type="button"
          onClick={() => setNextGaps(false)}
          className="btn-quiet text-[11px] mb-2"
          data-testid="exit-next-week-gaps"
        >
          &lsaquo; My queue
        </button>
        <SweeperWorkspace weekWindow="next" studioIds={scopedStudios} />
      </section>
    );
  }

  return (
    <section className="px-10 py-8" data-testid="operator-queue-screen">
      <header className="max-w-6xl flex items-start justify-between gap-6">
        <div>
          <h1 className="heading-display text-3xl text-cream">My Queue</h1>
          <p className="font-serif text-cream-soft mt-2 text-base">
            Calling leads to book from next week on.
          </p>
        </div>
        <div className="flex gap-3 text-right">
          <StatTile label="To call" value={toCallCount} accent testid="stat-to-call" />
          <StatTile label="Done" value={doneCount} testid="stat-done" />
        </div>
      </header>

      <div className="mt-8 grid grid-cols-12 gap-6 max-w-screen-2xl">
        <div className="col-span-12 lg:col-span-7">
          {gapFilter ? (
            <>
              <div
                data-testid="gap-fill-banner"
                className="rounded-lg border border-mint/40 bg-mint/5 px-4 py-3 mb-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Target size={18} className="text-mint shrink-0" />
                  <div>
                    <p className="label-small-caps text-mint">Filling a gap</p>
                    <p className="font-serif text-cream mt-1">
                      {filterArtist?.displayName ?? gapFilter.artistId} ·{' '}
                      {format(new Date(gapFilter.dayIso), 'EEE d MMM')}. Ranked by best match for
                      this slot.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGapFilter(null)}
                  className="btn-quiet text-[11px]"
                  data-testid="exit-gap-fill"
                >
                  <XCircle size={12} /> Exit gap-fill
                </button>
              </div>
              <ArtistDaySchedule artistId={gapFilter.artistId} dayIso={gapFilter.dayIso} />
              <div
                data-testid="gap-fill-coaching"
                className="font-serif text-sm text-cream-soft mb-3 px-1 leading-relaxed"
              >
                Top of the list has the strongest signals AND open calendars near this
                gap, easiest to close in time. Further down sits the reactivation tail
                bigger lift, bigger value if you land it. Click &quot;Why this match?&quot; on any
                row to see the reasoning.
              </div>
              <QueueDialer
                items={queue}
                onOpen={onOpen}
                emptyState="No strong candidates for this gap yet. Try a broader segment from Reactivation."
              />
            </>
          ) : (
            <>
              <div className="mb-4 space-y-2">
                <div className="flex items-center gap-1" data-testid="queue-seq-tabs">
                  {SEQ_TABS.map((tab) => {
                    const active = !showAlerts && seqTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => onSwitchSeqTab(tab.key)}
                        className={clsx(
                          'px-3 py-1.5 text-xs rounded-md border transition',
                          tab.key === 'reds' && !active
                            ? 'border-blood/40 text-blood/90 hover:border-blood'
                            : active && tab.key === 'reds'
                              ? 'border-blood text-blood bg-blood/10'
                              : active
                                ? 'border-gold-bright text-gold-bright bg-gold/10'
                                : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
                        )}
                        data-testid={`queue-seq-${tab.key}`}
                      >
                        {tab.label}{' '}
                        <span className="ml-1 font-mono text-[10px] text-cream-faint">
                          {seqCounts[tab.key]}
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowAlerts(true)}
                    className={clsx(
                      'px-3 py-1.5 text-xs rounded-md border transition',
                      showAlerts
                        ? 'border-blood text-blood bg-blood/10'
                        : alerts.length > 0
                          ? 'border-blood/50 text-blood hover:border-blood'
                          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
                    )}
                    data-testid="queue-alerts-tab"
                  >
                    Alerts{' '}
                    {alerts.length > 0 ? (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-blood px-1.5 py-px font-mono text-[10px] font-semibold text-cream align-middle">
                        <span className="w-1.5 h-1.5 rounded-full bg-cream/90 motion-safe:animate-pulse" />
                        {alerts.length}
                      </span>
                    ) : (
                      <span className="ml-1 font-mono text-[10px] text-cream-faint">0</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNextGaps(true)}
                    className="ml-auto px-3 py-1.5 text-xs rounded-md border border-gold-dim/30 text-cream-soft hover:border-gold-bright/40 transition"
                    data-testid="queue-next-week-gaps"
                  >
                    Next week gaps
                  </button>
                </div>
              </div>

              {showAlerts ? (
                <div className="space-y-4" data-testid="queue-alerts">
                  {alerts.length > 0 ? (
                    <div
                      className="flex items-center gap-3 rounded-lg border border-gold-dim/30 bg-gold/5 px-4 py-3"
                      data-testid="queue-alerts-banner"
                    >
                      <Bell size={16} className="text-gold-bright shrink-0" />
                      <p className="font-serif text-sm text-cream-soft flex-1">
                        <span className="text-cream font-semibold">
                          {alerts.length} new since your last batch
                        </span>{' '}
                        — they&rsquo;ll join your working set when you clear it.
                      </p>
                      <button
                        type="button"
                        onClick={promoteAlerts}
                        className="shrink-0 rounded-md border border-gold-dim/40 px-3 py-1.5 text-[11px] font-semibold text-cream-soft transition hover:border-gold-bright hover:text-cream"
                        data-testid="queue-alerts-promote"
                      >
                        Add to working set now
                      </button>
                    </div>
                  ) : null}

                  {alerts.length === 0 ? (
                    <p
                      className="panel-dark p-6 text-center font-serif text-cream-soft"
                      data-testid="queue-alerts-empty"
                    >
                      Nothing new. Missed calls, missed top-ups and fresh replies land here as
                      they happen.
                    </p>
                  ) : (
                    <ul>
                      {alerts.map((row) => (
                        <AlertItem key={row.id} row={row} onAction={() => onOpenAlert(row)} />
                      ))}
                    </ul>
                  )}

                  <p
                    className="flex items-center gap-2 font-serif text-xs text-cream-faint pt-1"
                    data-testid="queue-alerts-footer"
                  >
                    <Clock size={13} className="text-cream-faint shrink-0" />
                    Alerts collect here as they happen and join your working set in batches, so
                    calls aren&rsquo;t interrupted.
                  </p>
                </div>
              ) : (
              <>
              {scheduledDue.length > 0 ? (
                <section
                  className="mb-4 rounded-lg border border-gold-bright/50 bg-gold/5 p-3 animate-screen-pop-in animate-ring-pulse"
                  data-testid="queue-scheduled-due"
                >
                  <p className="label-small-caps text-gold-bright mb-2">
                    Scheduled call or message · due in 15 min
                  </p>
                  <QueueDialer items={scheduledDue} onOpen={onOpen} />
                </section>
              ) : null}

              {seqTab === 'reds' ? (
                <div className="space-y-4" data-testid="queue-reds">
                  <QueueDialer
                    items={lightReds}
                    onOpen={onOpen}
                    emptyState="No light reds. Nobody has run out the full call ladder unanswered."
                  />
                  <details
                    className="rounded-lg border border-blood/30 bg-blood/5 px-4 py-3"
                    data-testid="queue-dark-reds"
                  >
                    <summary className="cursor-pointer font-inter text-[11px] uppercase tracking-widest text-blood/90">
                      Declined · handed to automated outreach{' '}
                      <span className="font-mono text-cream-faint">{darkReds.length}</span>
                    </summary>
                    <p
                      className="font-serif text-sm text-cream-soft mt-2 mb-3"
                      data-testid="queue-dark-reds-banner"
                    >
                      Automated outreach handles this list (coming later).
                    </p>
                    <QueueDialer
                      items={darkReds}
                      onOpen={onOpen}
                      emptyState="Nobody has said no yet."
                    />
                  </details>
                </div>
              ) : clearedSequence ? (
                <>
                  <HandoffBanner
                    from={seqTab}
                    auto={willAutoAdvance}
                    onGaps={() => setNextGaps(true)}
                  />
                  <CompletedSequenceDrawer
                    items={completedForTab}
                    stage={seqTab}
                    nowIso={clockIso}
                    onOverride={onOpen}
                  />
                </>
              ) : (
                <>
                  {activeForTab.length > 0 ? (
                    <div className="mb-3 space-y-1.5" data-testid="queue-working-set">
                      <p className="font-serif text-xs text-cream-soft">
                        Working set —{' '}
                        <span className="text-cream font-semibold">
                          {workingSet.length} of {activeForTab.length}
                        </span>
                        {lockedCount > 0 ? ' · the rest unlock as you clear these' : ''}
                      </p>
                      <div className="h-1 rounded-full bg-ink-soft overflow-hidden">
                        <div
                          className="h-full bg-gold-bright rounded-full transition-all"
                          style={{
                            width: `${(workingSet.length / activeForTab.length) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                  <QueueDialer
                    items={workingSet}
                    onOpen={onOpen}
                    upNext
                    emptyState={
                      completedForTab.length > 0
                        ? 'Every lead in this sequence is contacted — see Completed below.'
                        : 'Nothing sitting in this sequence stage right now.'
                    }
                  />
                  {lockedCount > 0 ? (
                    <p
                      className="mt-3 flex items-center gap-2 font-serif text-xs text-cream-faint"
                      data-testid="queue-working-set-footer"
                    >
                      <Lock size={13} className="text-cream-faint shrink-0" />
                      {lockedCount} more unlock as you work through these
                    </p>
                  ) : null}
                  {completedForTab.length > 0 ? (
                    <CompletedSequenceDrawer
                      items={completedForTab}
                      stage={seqTab}
                      nowIso={clockIso}
                      onOverride={onOpen}
                    />
                  ) : null}
                </>
              )}
              </>
              )}
            </>
          )}
        </div>

        <aside className="col-span-12 lg:col-span-5 lg:sticky lg:top-4 self-start space-y-4">
          {gapFilter ? (
            <ArtistAvailabilityHeatmap
              rows={heatmapRows}
              studios={studios}
              selected={gapFilter}
              onSelect={onSelectCell}
              onClear={() => setGapFilter(null)}
            />
          ) : (
            <>
              <RailPanel
                title="Scheduled calls & messages"
                count={scheduledCalls.length}
                testid="rail-scheduled-calls"
              >
                {scheduledCalls.length === 0 ? (
                  <RailEmpty>Nothing scheduled later today.</RailEmpty>
                ) : (
                  <ul className="space-y-2">
                    {scheduledCalls.map((item) => (
                      <ScheduledRow key={item.id} item={item} />
                    ))}
                  </ul>
                )}
              </RailPanel>

              <div className="panel-dark" data-testid="rail-availability">
                <button
                  type="button"
                  onClick={() => setShowAvailability((v) => !v)}
                  aria-expanded={showAvailability}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  data-testid="rail-availability-toggle"
                >
                  <span className="label-small-caps text-gold-bright">Show availability</span>
                  {showAvailability ? (
                    <ChevronUp size={14} className="text-cream-soft" />
                  ) : (
                    <ChevronDown size={14} className="text-cream-soft" />
                  )}
                </button>
                {showAvailability ? (
                  <div className="px-3 pb-3">
                    <ArtistAvailabilityHeatmap
                      rows={heatmapRows}
                      studios={studios}
                      selected={gapFilter}
                      onSelect={onSelectCell}
                      onClear={() => setGapFilter(null)}
                    />
                  </div>
                ) : null}
              </div>
            </>
          )}
        </aside>
      </div>

      <QueueWorkModal
        item={modalItem}
        initialChannel={modalChannel}
        onClose={() => setModalItem(null)}
      />
    </section>
  );
}

function RailPanel({
  title,
  count,
  testid,
  children,
}: {
  title: string;
  count: number;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <div className="panel-dark px-4 py-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="label-small-caps text-gold-bright">{title}</p>
        <span className="font-mono text-[10px] text-cream-faint">{count}</span>
      </div>
      {children}
    </div>
  );
}

function RailEmpty({ children }: { children: React.ReactNode }) {
  return <p className="font-serif text-sm text-cream-faint">{children}</p>;
}

function ScheduledRow({ item }: { item: QueueItem }) {
  const isVoice = item.recommendedChannel === 'voice';
  const ChannelIcon = isVoice ? Phone : MessageSquare;
  return (
    <li
      className="flex items-center gap-3 rounded-md border border-gold-dim/25 bg-ink-deep px-3 py-2"
      data-testid={`rail-scheduled-${item.customer.id}`}
    >
      <ChannelIcon size={13} className="text-gold-bright shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-inter text-sm text-cream truncate">
          {item.customer.preferredName ?? `${item.customer.firstName} ${item.customer.lastName}`}
        </p>
        {item.recommendedAt ? (
          <p className="font-serif text-[11px] text-cream-soft mt-0.5">
            {isVoice ? 'Call' : 'Message'} at{' '}
            <span className="text-gold">{simTimeShort(item.recommendedAt)}</span>
          </p>
        ) : null}
      </div>
      {item.recommendedAt ? (
        <span className="font-mono text-[10px] text-cream-faint uppercase tracking-widest shrink-0">
          {relativeFromIso(item.recommendedAt)}
        </span>
      ) : null}
    </li>
  );
}

const REPLY_CHANNEL_LABEL: Record<Channel, string> = {
  sms: 'SMS',
  'meta-dm': 'Messenger',
  'ig-dm': 'IG',
  email: 'Email',
  voice: 'Call',
  'web-form': 'Web',
  'walk-in': 'Walk-in',
};

/**
 * Automatic sequence hand-off (bd tv-1x7). When a call sequence is cleared, a
 * success banner shows and the next sequence loads on its own (First -> Second
 * -> Third). After the third there's nothing to auto-load, so it offers the
 * future-gaps board instead.
 */
function HandoffBanner({
  from,
  auto,
  onGaps,
}: {
  from: SeqTab;
  auto: boolean;
  onGaps: () => void;
}) {
  const isThird = from === 'third';
  const next = from === 'first' ? 'Second' : from === 'second' ? 'Third' : null;
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-mint/40 bg-mint/5 px-4 py-3 mb-4"
      data-testid="queue-handoff-banner"
    >
      <CheckCircle2 size={18} className="text-mint shrink-0" />
      <div className="flex-1 min-w-0">
        {isThird ? (
          <p className="font-serif text-sm text-cream-soft">
            <span className="text-cream font-semibold">All three sequences cleared.</span> You&rsquo;ve
            reached everyone you can for now &mdash; go fill gaps further out.
          </p>
        ) : auto ? (
          <p className="font-serif text-sm text-cream-soft">
            <span className="text-cream font-semibold">{SEQ_LABEL[from]} sequence cleared.</span>{' '}
            Loading {next} sequence&hellip;
          </p>
        ) : (
          <p className="font-serif text-sm text-cream-soft">
            <span className="text-cream font-semibold">{SEQ_LABEL[from]} sequence cleared.</span>{' '}
            Every lead here is contacted. Pick another sequence when you&rsquo;re ready.
          </p>
        )}
      </div>
      {isThird ? (
        <button
          type="button"
          onClick={onGaps}
          className="shrink-0 rounded-md bg-mint px-4 py-2 text-xs font-semibold text-cream transition hover:opacity-90"
          data-testid="queue-handoff-gaps"
        >
          Fill future gaps
        </button>
      ) : auto ? (
        <span
          className="shrink-0 flex items-center gap-2 font-inter text-[11px] uppercase tracking-widest text-mint"
          data-testid="queue-handoff-loading"
        >
          Auto
          <span className="w-1.5 h-1.5 rounded-full bg-mint motion-safe:animate-pulse" />
        </span>
      ) : null}
    </div>
  );
}

// The sequence a dormant lead rolls into next once its 24h cooldown lifts.
const NEXT_SEQ_LABEL: Record<SeqTab, string> = {
  first: 'Second sequence',
  second: 'Third sequence',
  third: 'final follow-up',
  reds: '',
};

// "Xh Ym" until the given ISO instant, relative to now.
function formatCountdown(nowIso: string, dueIso: string): string {
  const ms = new Date(dueIso).getTime() - new Date(nowIso).getTime();
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Contacted leads (dormant, in their 24h cooldown) collect here rather than
 * cluttering the active focus batch (bd tv-jpx). Each shows a countdown to the
 * next sequence and a "Call now" override that reaches them early on purpose.
 */
function CompletedSequenceDrawer({
  items,
  stage,
  nowIso,
  onOverride,
}: {
  items: QueueItem[];
  stage: SeqTab;
  nowIso: string;
  onOverride: (item: QueueItem) => void;
}) {
  const nextLabel = NEXT_SEQ_LABEL[stage];
  return (
    <details
      className="mt-4 rounded-lg border border-gold-dim/25 bg-ink-soft/40 overflow-hidden"
      data-testid="queue-completed-drawer"
    >
      <summary className="cursor-pointer flex items-center gap-2 px-4 py-3 font-inter text-[11px] uppercase tracking-widest text-cream-soft">
        <CheckCircle2 size={14} className="text-mint" />
        Completed this sequence{' '}
        <span className="font-mono text-cream-faint">{items.length}</span>
      </summary>
      <ul>
        {items.map((item) => {
          const name =
            item.customer.preferredName ??
            `${item.customer.firstName} ${item.customer.lastName}`;
          const last = item.customer.conversation[item.customer.conversation.length - 1];
          return (
            <li
              key={item.id}
              className="flex items-center gap-3 px-4 py-3 border-t border-gold-dim/12 opacity-80"
              data-testid={`queue-completed-${item.customer.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-inter text-[14px] text-cream font-semibold truncate">
                    {name}
                  </span>
                  {item.sequenceDueAtIso && nextLabel ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-ink-soft px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      <Lock size={10} className="text-gold-bright" />
                      {nextLabel} in{' '}
                      <span className="font-mono text-cream normal-case tracking-normal">
                        {formatCountdown(nowIso, item.sequenceDueAtIso)}
                      </span>
                    </span>
                  ) : null}
                </div>
                <p className="font-serif text-[12px] text-cream-soft mt-0.5 truncate">
                  Contacted{last ? ` · “${last.body}”` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onOverride(item)}
                className="shrink-0 rounded-md border border-gold-dim/40 px-3 py-1.5 text-[11px] font-semibold text-gold-bright transition hover:border-gold-bright"
                data-testid={`queue-completed-override-${item.customer.id}`}
              >
                Call now
              </button>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

const ALERT_ICON: Record<AlertKind, typeof PhoneMissed> = {
  'no-deposit-declined': Ban,
  'missed-call': PhoneMissed,
  'missed-topup': CircleDollarSign,
  'new-reply': MessageSquare,
};

function AlertItem({ row, onAction }: { row: AlertRow; onAction: () => void }) {
  const meta = ALERT_META[row.alertKind];
  const Icon = ALERT_ICON[row.alertKind];
  const urgent = row.alertKind !== 'new-reply';
  const name =
    row.customer.preferredName ?? `${row.customer.firstName} ${row.customer.lastName}`;
  return (
    <li
      className="flex items-center gap-4 border-t border-gold-dim/15 py-3.5 first:border-t-0"
      data-testid={`queue-alert-${row.customer.id}`}
    >
      <div
        className={clsx(
          'shrink-0 w-9 h-9 rounded-full grid place-items-center',
          urgent ? 'bg-blood/10' : 'bg-ink-soft',
        )}
      >
        <Icon size={17} className={urgent ? 'text-blood' : 'text-cream-soft'} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
          {meta.label}
        </p>
        <p className="font-serif text-cream mt-0.5 truncate">
          <span className="font-semibold">{name}</span>{' '}
          <span className="text-cream-soft">&mdash; {row.detail}</span>
        </p>
        {row.whenIso ? (
          <p className="font-serif text-[11px] text-cream-faint mt-0.5">
            {relativeFromIso(row.whenIso)}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onAction}
        className={clsx(
          'shrink-0 rounded-md px-4 py-2 text-xs font-semibold transition',
          urgent
            ? 'bg-gold-bright text-ink-black hover:bg-gold'
            : 'border border-gold-dim/40 text-cream-soft hover:border-gold-bright hover:text-cream',
        )}
        data-testid={`queue-alert-action-${row.customer.id}`}
      >
        {meta.action}
      </button>
    </li>
  );
}

function StatTile({
  label,
  value,
  accent,
  testid,
}: {
  label: string;
  value: number;
  accent?: boolean;
  testid?: string;
}) {
  return (
    <div
      data-testid={testid}
      className={clsx(
        'panel-dark px-4 py-2 min-w-[80px]',
        accent ? 'border-gold-bright/50' : '',
      )}
    >
      <p
        className={clsx(
          'font-cinzel text-2xl',
          accent ? 'text-gold-bright' : 'text-cream',
        )}
      >
        {value}
      </p>
      <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-0.5">
        {label}
      </p>
    </div>
  );
}
